import { useCallback, useEffect, useMemo, useState } from "react";
import { Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import CredentialHealthStrip from "../credentials/CredentialHealthStrip.jsx";
import { summarizeFindingDrift } from "../../utils/postureScore.js";

const RUN_STATE_STYLE = {
  SUCCESS: { color: "var(--healthy)", label: "Success" },
  FAILED: { color: "var(--critical)", label: "Failed" },
  MISSED: { color: "var(--warning)", label: "Missed" },
  RUNNING: { color: "var(--accent)", label: "Running" },
};

function formatWhen(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return String(iso);
  }
}

function RunHistoryRow({ run }) {
  const style = RUN_STATE_STYLE[run.runState] || RUN_STATE_STYLE.FAILED;
  return (
    <div
      className="flex flex-wrap items-start gap-2 border-b py-2 text-[12px]"
      style={{ borderColor: "var(--border)" }}
    >
      <span
        className="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide"
        style={{ background: `${style.color}22`, color: style.color }}
      >
        {style.label}
      </span>
      <span className="text-dim shrink-0">{formatWhen(run.scheduledAt)}</span>
      {run.failureReason ? (
        <span className="min-w-0 flex-1 text-slate-400">
          {run.failureReason}
          {run.failureDetail ? ` — ${run.failureDetail}` : ""}
        </span>
      ) : run.snapshotId ? (
        <span className="text-dim">snapshot stored</span>
      ) : null}
    </div>
  );
}

function TrendsDetailPanel({ snapshots, detail, busy, driftSummary, driftChanges }) {
  const snaps = Array.isArray(snapshots) ? snapshots : [];
  const data = snaps
    .slice()
    .reverse()
    .map((s) => ({
      date: (() => {
        try {
          return new Date(s.takenAt).toLocaleDateString();
        } catch {
          return String(s.takenAt || "").slice(0, 10) || "—";
        }
      })(),
      critical: Number(s?.summary?.criticalCount ?? 0),
      high: Number(s?.summary?.highCount ?? 0),
      medium: Number(s?.summary?.mediumCount ?? s?.summary?.warningCount ?? 0),
      low: Number(s?.summary?.lowCount ?? s?.summary?.infoCount ?? 0),
      warnings: Number(s?.summary?.mediumCount ?? s?.summary?.warningCount ?? 0),
    }));

  return (
    <div
      style={{
        border: "1px solid var(--border)",
        borderRadius: "8px",
        padding: "12px 12px",
        background: "var(--bg-elevated)",
        marginTop: "16px",
      }}
    >
      {(driftSummary?.items?.length > 0 || (Array.isArray(driftChanges) && driftChanges.length > 0)) ? (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-secondary)", marginBottom: 6 }}>
            What changed (full)
          </div>
          {driftSummary?.items?.length ? (
            <ul style={{ margin: 0, paddingLeft: 16, fontSize: 11, color: "var(--text-secondary)" }}>
              {driftSummary.items.slice(0, 8).map((item) => (
                <li key={item.id || item.label}>{item.label || item.id}</li>
              ))}
            </ul>
          ) : (
            <ul style={{ margin: 0, paddingLeft: 16, fontSize: 11, color: "var(--text-secondary)" }}>
              {driftChanges.slice(0, 8).map((c, i) => (
                <li key={c.id || i}>{c.message || c.type || JSON.stringify(c)}</li>
              ))}
            </ul>
          )}
        </div>
      ) : null}

      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-secondary)" }}>
          Finding counts over time
        </div>
        <div className="text-dim" style={{ fontSize: 11 }}>
          Detailed view — Dashboard shows posture score trend
        </div>
      </div>

      {snaps.length >= 2 ? (
        <div style={{ height: 130 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data}>
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: "rgba(255,255,255,0.7)" }} />
              <YAxis tick={{ fontSize: 10, fill: "rgba(255,255,255,0.7)" }} allowDecimals={false} />
              <Tooltip />
              <Line type="monotone" dataKey="critical" stroke="var(--critical)" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="warnings" stroke="var(--warning)" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <p className="text-dim" style={{ fontSize: 12, margin: 0 }}>
          Run at least two assessments to compare finding counts over time.
        </p>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 10 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-secondary)", marginBottom: 6 }}>
            Recently resolved
          </div>
          {busy ? (
            <div className="text-dim" style={{ fontSize: 11 }}>
              Computing…
            </div>
          ) : detail?.resolved?.length ? (
            <ul style={{ margin: 0, paddingLeft: 16, fontSize: 11, color: "var(--text-secondary)" }}>
              {detail.resolved.map((id) => (
                <li key={id}>{id}</li>
              ))}
            </ul>
          ) : (
            <div className="text-dim" style={{ fontSize: 11 }}>
              None detected.
            </div>
          )}
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-secondary)", marginBottom: 6 }}>
            Recently surfaced
          </div>
          {busy ? (
            <div className="text-dim" style={{ fontSize: 11 }}>
              Computing…
            </div>
          ) : detail?.surfaced?.length ? (
            <ul style={{ margin: 0, paddingLeft: 16, fontSize: 11, color: "var(--text-secondary)" }}>
              {detail.surfaced.map((id) => (
                <li key={id}>{id}</li>
              ))}
            </ul>
          ) : (
            <div className="text-dim" style={{ fontSize: 11 }}>
              None detected.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** Monitoring tab — scheduled assessments, run ledger, trend history. */
export default function MonitoringTabContent({
  projectId = null,
  providerId = "okta",
  credentialHealth = null,
  snapshots = [],
  auditData = null,
  trendDetail = null,
  trendDetailBusy = false,
  driftData = null,
  priorSnapshot = null,
}) {
  const [status, setStatus] = useState(null);
  const [trendPoints, setTrendPoints] = useState([]);
  const [busy, setBusy] = useState(false);
  const [saveBusy, setSaveBusy] = useState(false);
  const [error, setError] = useState("");
  const [cadence, setCadence] = useState("daily");
  const [oktaToken, setOktaToken] = useState("");
  const [showTokenUpdate, setShowTokenUpdate] = useState(false);
  const [tokenNotice, setTokenNotice] = useState("");

  const refresh = useCallback(async () => {
    if (!projectId) return;
    setBusy(true);
    setError("");
    try {
      const [stRes, tpRes] = await Promise.all([
        fetch(`/api/projects/${encodeURIComponent(projectId)}/monitoring`, { credentials: "include" }),
        fetch(`/api/projects/${encodeURIComponent(projectId)}/monitoring/trend-points`, {
          credentials: "include",
        }),
      ]);
      const stData = await stRes.json().catch(() => ({}));
      const tpData = await tpRes.json().catch(() => ({}));
      if (!stRes.ok || stData.ok === false) throw new Error(stData.error || "Failed to load monitoring");
      setStatus(stData);
      if (stData.config?.cadence) setCadence(stData.config.cadence);
      setTrendPoints(Array.isArray(tpData.trendPoints) ? tpData.trendPoints : []);
    } catch (e) {
      setError(e?.message || "Failed to load monitoring");
    } finally {
      setBusy(false);
    }
  }, [projectId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const scoreTrend = useMemo(() => {
    if (trendPoints.length > 0) {
      return trendPoints.map((p) => ({
        date: (() => {
          try {
            return new Date(p.takenAt).toLocaleDateString();
          } catch {
            return String(p.takenAt || "").slice(0, 10);
          }
        })(),
        score: p.postureScore ?? 0,
      }));
    }
    const snaps = Array.isArray(snapshots) ? snapshots : [];
    return snaps
      .slice()
      .reverse()
      .map((s) => ({
        date: (() => {
          try {
            return new Date(s.takenAt).toLocaleDateString();
          } catch {
            return String(s.takenAt || "").slice(0, 10);
          }
        })(),
        score: s?.summary?.postureScore?.score ?? auditData?.postureScore?.score ?? 0,
      }));
  }, [trendPoints, snapshots, auditData]);

  const driftSummary = useMemo(() => {
    if (!priorSnapshot || !auditData) return null;
    try {
      return summarizeFindingDrift(priorSnapshot, auditData);
    } catch {
      return null;
    }
  }, [priorSnapshot, auditData]);

  const config = status?.config;
  const enabled = Boolean(config?.enabled);
  const isOkta = String(providerId || "").toLowerCase() === "okta";

  const handleSaveOktaToken = async () => {
    if (!projectId || !oktaToken.trim()) {
      setError("Enter a new SSWS token before saving.");
      return;
    }
    setSaveBusy(true);
    setError("");
    setTokenNotice("");
    try {
      const res = await fetch(`/api/projects/${encodeURIComponent(projectId)}/monitoring`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ oktaApiToken: oktaToken.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) {
        throw new Error(data.error || "Failed to update SSWS token");
      }
      setOktaToken("");
      setShowTokenUpdate(false);
      setTokenNotice("Server SSWS token updated.");
      await refresh();
    } catch (e) {
      setError(e?.message || "Failed to update SSWS token");
    } finally {
      setSaveBusy(false);
    }
  };

  const handleToggle = async (nextEnabled) => {
    if (!projectId) return;
    if (nextEnabled && isOkta && !config?.hasServerCredentials && !oktaToken.trim()) {
      setError("Enter an SSWS token in the field above, then enable monitoring.");
      return;
    }
    setSaveBusy(true);
    setError("");
    try {
      const body = { enabled: nextEnabled, cadence };
      if (nextEnabled && isOkta && oktaToken.trim()) {
        body.oktaApiToken = oktaToken.trim();
      }
      const res = await fetch(`/api/projects/${encodeURIComponent(projectId)}/monitoring`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) {
        if (data.needsCredentials) {
          throw new Error(
            "Okta monitoring needs a server-stored SSWS token. Enter it above, then enable monitoring.",
          );
        }
        throw new Error(data.error || "Failed to update monitoring");
      }
      setOktaToken("");
      await refresh();
    } catch (e) {
      setError(e?.message || "Failed to update monitoring");
    } finally {
      setSaveBusy(false);
    }
  };

  const handleCadenceChange = async (nextCadence) => {
    setCadence(nextCadence);
    if (!projectId || !enabled) return;
    setSaveBusy(true);
    try {
      const res = await fetch(`/api/projects/${encodeURIComponent(projectId)}/monitoring`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ cadence: nextCadence }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) throw new Error(data.error || "Failed to update cadence");
      await refresh();
    } catch (e) {
      setError(e?.message || "Failed to update cadence");
    } finally {
      setSaveBusy(false);
    }
  };

  return (
    <section style={{ padding: "16px 0 8px" }}>
      {status?.alert ? (
        <div
          className="mb-3 rounded-lg border px-3 py-2.5 text-[13px]"
          style={{
            borderColor: status.alert.level === "error" ? "var(--critical)" : "var(--warning)",
            background: status.alert.level === "error" ? "rgba(var(--critical-rgb,255,80,80)/0.08)" : "rgba(var(--warning-rgb,255,180,0)/0.08)",
            color: "var(--text-primary)",
          }}
        >
          {status.alert.message}
        </div>
      ) : null}

      <div
        style={{
          borderRadius: "8px",
          border: "1px solid var(--border)",
          background: "var(--bg-elevated)",
          padding: "16px 16px",
        }}
      >
        <div className="text-dim" style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.12em" }}>
          MONITORING
        </div>
        <p style={{ fontSize: 15, fontWeight: 600, color: "var(--text-primary)", marginTop: 8, marginBottom: 12 }}>
          Scheduled posture assessments
        </p>

        {isOkta && !config?.hasServerCredentials ? (
          <div className="mb-4 space-y-2">
            <p className="text-dim text-[11px]">
              Okta requires a server-stored SSWS token for unattended runs (browser vault is not available to the scheduler).
              Use a <strong>Read-only Administrator</strong> custom role token — not Super Admin.
            </p>
            <input
              type="password"
              value={oktaToken}
              onChange={(e) => setOktaToken(e.target.value)}
              placeholder="SSWS token (saved encrypted when enabling)"
              className="w-full max-w-md rounded border px-2 py-1.5 text-[12px]"
              style={{ borderColor: "var(--border)", background: "var(--bg-input)", color: "var(--text-primary)" }}
            />
          </div>
        ) : null}

        {isOkta && config?.hasServerCredentials ? (
          <div className="mb-4 space-y-2">
            {!showTokenUpdate ? (
              <button
                type="button"
                disabled={saveBusy || busy}
                onClick={() => {
                  setShowTokenUpdate(true);
                  setTokenNotice("");
                  setError("");
                }}
                className="text-[12px] font-medium underline-offset-2 hover:underline"
                style={{ color: "var(--accent)" }}
              >
                Update server SSWS token
              </button>
            ) : (
              <>
                <p className="text-dim text-[11px]">
                  Paste a new token to replace the one stored for scheduled runs. Browser session tokens are unchanged.
                </p>
                <input
                  type="password"
                  value={oktaToken}
                  onChange={(e) => setOktaToken(e.target.value)}
                  placeholder="New SSWS token"
                  className="w-full max-w-md rounded border px-2 py-1.5 text-[12px]"
                  style={{ borderColor: "var(--border)", background: "var(--bg-input)", color: "var(--text-primary)" }}
                />
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    disabled={saveBusy || busy || !oktaToken.trim()}
                    onClick={() => void handleSaveOktaToken()}
                    className="rounded border px-3 py-1 text-[12px] font-medium"
                    style={{
                      borderColor: "var(--border-bright)",
                      background: "var(--bg-input)",
                      color: "var(--text-primary)",
                    }}
                  >
                    Save new token
                  </button>
                  <button
                    type="button"
                    disabled={saveBusy || busy}
                    onClick={() => {
                      setShowTokenUpdate(false);
                      setOktaToken("");
                      setError("");
                    }}
                    className="rounded border px-3 py-1 text-[12px]"
                    style={{ borderColor: "var(--border)", color: "var(--text-secondary)" }}
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        ) : null}

        <div className="flex flex-wrap items-center gap-4">
          <label className="flex cursor-pointer items-center gap-2 text-[13px] text-slate-200">
            <input
              type="checkbox"
              checked={enabled}
              disabled={saveBusy || busy}
              onChange={(e) => void handleToggle(e.target.checked)}
            />
            Enable monitoring
          </label>
          <label className="flex items-center gap-2 text-[13px] text-slate-300">
            Cadence
            <select
              value={cadence}
              disabled={saveBusy || busy}
              onChange={(e) => void handleCadenceChange(e.target.value)}
              className="rounded border px-2 py-1 text-[12px]"
              style={{ borderColor: "var(--border)", background: "var(--bg-input)", color: "var(--text-primary)" }}
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
            </select>
          </label>
        </div>

        {credentialHealth?.hasCredentials || status?.credentialHealth?.hasCredentials ? (
          <div className="mt-3 max-w-lg">
            <CredentialHealthStrip health={credentialHealth || status?.credentialHealth} compact />
          </div>
        ) : null}

        <div className="mt-3 grid gap-1 text-[12px] text-slate-400 sm:grid-cols-3">
          <div>
            <span className="text-dim">Last run: </span>
            {formatWhen(status?.lastRun?.finishedAt || status?.lastRun?.scheduledAt)}
          </div>
          <div>
            <span className="text-dim">Next run: </span>
            {enabled ? formatWhen(config?.nextFireAt) : "— (disabled)"}
          </div>
          <div>
            <span className="text-dim">Status: </span>
            {status?.hasRunning ? "Running…" : status?.lastRun?.runState || "—"}
          </div>
        </div>

        {tokenNotice ? (
          <p className="mt-2 text-[12px]" style={{ color: "var(--healthy)" }}>
            {tokenNotice}
          </p>
        ) : null}

        {error ? (
          <p className="mt-2 text-[12px]" style={{ color: "var(--critical)" }}>
            {error}
          </p>
        ) : null}
      </div>

      {scoreTrend.length > 0 ? (
        <div style={{ marginTop: 16 }}>
          <div className="text-secondary" style={{ fontSize: 11, fontWeight: 600, marginBottom: 6 }}>
            Posture score history (from trend points)
          </div>
          <div
            style={{
              height: 140,
              border: "1px solid var(--border)",
              borderRadius: 8,
              padding: "8px 8px 0",
              background: "var(--bg-elevated)",
            }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={scoreTrend}>
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "rgba(255,255,255,0.65)" }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: "rgba(255,255,255,0.65)" }} />
                <Tooltip
                  contentStyle={{
                    background: "var(--bg-elevated)",
                    border: "1px solid var(--border)",
                    fontSize: 11,
                  }}
                />
                <Line type="monotone" dataKey="score" stroke="var(--accent)" strokeWidth={2} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      ) : null}

      <div
        style={{
          marginTop: 16,
          border: "1px solid var(--border)",
          borderRadius: 8,
          padding: "12px 12px",
          background: "var(--bg-elevated)",
        }}
      >
        <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-secondary)", marginBottom: 8 }}>
          Run history
        </div>
        {busy && !status?.runs?.length ? (
          <p className="text-dim text-[12px]">Loading…</p>
        ) : status?.runs?.length ? (
          status.runs.map((run) => <RunHistoryRow key={run.runId} run={run} />)
        ) : (
          <p className="text-dim text-[12px]">No scheduled runs yet. Enable monitoring to start the ledger.</p>
        )}
      </div>

      <TrendsDetailPanel
        snapshots={snapshots}
        detail={trendDetail}
        busy={trendDetailBusy}
        driftSummary={driftSummary}
        driftChanges={driftData?.changes}
      />
    </section>
  );
}
